import { useMemo, useState, type ReactNode } from "react";
import { Check, Search, X } from "lucide-react";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Input, cn } from "@ncp/ui";

export interface RefOption { key: string; name: string; nameAr?: string | null; hint?: string | null; badge?: ReactNode; description?: string | null }

interface Props {
  options: RefOption[];
  value: string[];
  onChange: (next: string[]) => void;
  readOnly?: boolean;
  /** Placeholder for the filter box; the box appears when there are more than eight options. */
  searchPlaceholder?: string;
  emptyLabel?: ReactNode;
  /** Renders as a compact chip list (selected first) instead of a checklist. */
  className?: string;
  /** Max height of the list before it scrolls. */
  maxHeight?: string;
}

/** Multi-select over keyed references (agents, collections, roles, solutions…) with a filter box and selected-first ordering. */
export function RefPicker({ options, value, onChange, readOnly, searchPlaceholder, emptyLabel, className, maxHeight = "max-h-72" }: Props) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [q, setQ] = useState("");
  const label = (o: RefOption) => (locale === "ar" && o.nameAr ? o.nameAr : o.name);
  const selected = useMemo(() => new Set(value), [value]);
  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = term ? options.filter((o) => o.key.toLowerCase().includes(term) || o.name.toLowerCase().includes(term) || (o.nameAr ?? "").includes(term) || (o.hint ?? "").toLowerCase().includes(term)) : options;
    return [...list].sort((a, b) => Number(selected.has(b.key)) - Number(selected.has(a.key)) || a.name.localeCompare(b.name));
  }, [options, q, selected]);
  const toggle = (key: string) => {
    if (readOnly) return;
    onChange(selected.has(key) ? value.filter((k) => k !== key) : [...value, key]);
  };
  // Keys that are selected but unknown to the option list (removed definitions) stay visible so they can be cleared.
  const orphans = value.filter((k) => !options.some((o) => o.key === k));

  return (
    <div className={cn("rounded-xl border border-sand-200 bg-white/80", className)}>
      {options.length > 8 ? (
        <div className="relative border-b border-sand-100 p-2">
          <Search className="pointer-events-none absolute start-4 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={searchPlaceholder ?? t("admin:common.search")} className="h-8 ps-8 text-xs" />
        </div>
      ) : null}
      <ul className={cn("divide-y divide-sand-100 overflow-auto scrollbar-thin", maxHeight)} role="listbox" aria-multiselectable>
        {orphans.map((k) => (
          <li key={k} className="flex items-center gap-2 px-3 py-2 text-xs">
            <span className="grid size-4 place-items-center rounded border border-warning-300 bg-warning-50 text-warning-700"><Check className="size-3" /></span>
            <code className="font-mono" dir="ltr">{k}</code>
            <Badge variant="warning" size="sm">{t("admin:workspaces.picker.unknown")}</Badge>
            {!readOnly ? <button type="button" className="ms-auto text-muted-foreground hover:text-foreground" onClick={() => toggle(k)} aria-label={t("admin:common.remove")}><X className="size-3.5" /></button> : null}
          </li>
        ))}
        {rows.length === 0 && orphans.length === 0 ? <li className="px-3 py-4 text-center text-xs text-muted-foreground">{emptyLabel ?? t("admin:common.empty")}</li> : null}
        {rows.map((o) => {
          const on = selected.has(o.key);
          return (
            <li key={o.key}>
              <button type="button" role="option" aria-selected={on} disabled={readOnly} onClick={() => toggle(o.key)} className={cn("flex w-full items-start gap-2.5 px-3 py-2 text-start text-sm transition-colors", on ? "bg-aegold-50/50" : "hover:bg-sand-50", readOnly && "cursor-default")}>
                <span className={cn("mt-0.5 grid size-4 shrink-0 place-items-center rounded border", on ? "border-aegold-500 bg-aegold-500 text-white" : "border-sand-300 bg-white")}>{on ? <Check className="size-3" /> : null}</span>
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-1.5"><span className="font-medium">{label(o)}</span><code className="font-mono text-[11px] text-muted-foreground" dir="ltr">{o.key}</code>{o.badge}</span>
                  {o.description || o.hint ? <span className="block truncate text-xs text-muted-foreground">{o.description ?? o.hint}</span> : null}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <div className="flex items-center justify-between border-t border-sand-100 px-3 py-1.5 text-[11px] text-muted-foreground">
        <span>{t("admin:workspaces.picker.selected", { count: value.length })}</span>
        {!readOnly && value.length ? <button type="button" className="hover:text-foreground" onClick={() => onChange([])}>{t("admin:common.clear")}</button> : null}
      </div>
    </div>
  );
}

/** Read-only chips for a list of keys, resolving names from an option list when available. */
export function KeyChips({ keys, options, max = 6, empty }: { keys: string[]; options?: RefOption[]; max?: number; empty?: ReactNode }) {
  const { locale } = useLocale();
  if (!keys.length) return <span className="text-xs text-muted-foreground">{empty ?? "—"}</span>;
  const shown = keys.slice(0, max);
  return (
    <span className="flex flex-wrap items-center gap-1">
      {shown.map((k) => {
        const o = options?.find((x) => x.key === k);
        return <Badge key={k} variant="outline" size="sm" className={o ? undefined : "font-mono"}>{o ? (locale === "ar" && o.nameAr ? o.nameAr : o.name) : k}</Badge>;
      })}
      {keys.length > max ? <Badge variant="outline" size="sm">+{keys.length - max}</Badge> : null}
    </span>
  );
}

const PALETTE = ["gold", "sea", "camel", "sand", "green", "red", "dark"] as const;
const SWATCH: Record<(typeof PALETTE)[number], string> = { gold: "bg-aegold-500", sea: "bg-seablue-500", camel: "bg-camel-500", sand: "bg-sand-400", green: "bg-success-500", red: "bg-danger-500", dark: "bg-aeblack-950" };

/** Colour token picker for tiles and badges. */
export function ColorPicker({ value, onChange, readOnly }: { value: string; onChange: (c: string) => void; readOnly?: boolean }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {PALETTE.map((c) => (
        <button key={c} type="button" disabled={readOnly} aria-label={c} aria-pressed={value === c} onClick={() => onChange(c)} className={cn("size-7 rounded-full border-2 transition-transform", SWATCH[c], value === c ? "scale-110 border-aeblack-950" : "border-transparent hover:scale-105", readOnly && "cursor-default")} />
      ))}
    </div>
  );
}

/** Common lucide icon names offered for workspaces, assistants and collections; anything else can be typed. */
export const ICON_SUGGESTIONS = ["Building2", "Landmark", "Scale", "Gavel", "BookOpen", "Library", "Sparkles", "Bot", "Briefcase", "Globe", "LineChart", "BarChart3", "FileText", "Users", "ShieldCheck", "Handshake", "Factory", "Store", "Coins", "Newspaper", "Megaphone", "GraduationCap", "Leaf", "Plane"];
